import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { AuthService } from '@core/services/auth.service';
import { HeaderService } from '@core/services/header.service';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { environment } from '../../../environments/environment';

@Component({
  standalone: false,
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  employee: any = {};
  departments: any[] = [];
  loading = true;
  saving = false;
  activeSection = 'personal';
  editMode = false;
  teamMembers: any[] = [];

  sections = [
    { key: 'personal', label: 'Personal Info', icon: 'person' },
    { key: 'contact', label: 'Contact Details', icon: 'phone' },
    { key: 'emergency', label: 'Emergency Contact', icon: 'health_and_safety' },
    { key: 'employment', label: 'Employment', icon: 'work' },
    { key: 'bank', label: 'Bank Details', icon: 'account_balance' },
    { key: 'address', label: 'Address', icon: 'location_on' },
    { key: 'education', label: 'Education', icon: 'school' },
    { key: 'experience', label: 'Experience', icon: 'work_history' },
    { key: 'documents', label: 'Documents', icon: 'folder_open' },
    { key: 'team', label: 'My Team', icon: 'groups' }
  ];

  candidateFormData: any = null;
  candidateEducation: any[] = [];
  candidateExperience: any[] = [];

  documents: any[] = [];
  documentTypes = [
    { key: 'aadhaar', label: 'Aadhaar Card', icon: 'credit_card' },
    { key: 'pan', label: 'PAN Card', icon: 'credit_card' },
    { key: 'photo', label: 'Passport Photo', icon: 'photo_camera' },
    { key: 'passport', label: 'Passport', icon: 'flight' },
    { key: 'offer_letter', label: 'Offer Letter', icon: 'description' },
    { key: 'experience_letter', label: 'Experience Letter', icon: 'workspace_premium' },
    { key: 'education_cert', label: 'Education Certificate', icon: 'school' },
    { key: 'other', label: 'Other', icon: 'attach_file' }
  ];

  genderOptions = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
    { label: 'Other', value: 'other' }
  ];

  countries: string[] = [];
  presentStates: string[] = [];
  presentCities: string[] = [];
  permanentStates: string[] = [];
  permanentCities: string[] = [];
  loadingCountries = false;
  loadingPresentStates = false;
  loadingPresentCities = false;
  loadingPermanentStates = false;
  loadingPermanentCities = false;

  private geoApiBase = 'https://countriesnow.space/api/v0.1';

  constructor(
    private authService: AuthService,
    private headerService: HeaderService,
    private apiService: ApiService,
    private snackbarService: SnackbarService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.headerService.setTitle('My Profile');
    this.loadCountries();
    this.loadProfile();
  }

  loadProfile(): void {
    this.loading = true;
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser?.id) { this.loading = false; return; }

    forkJoin({
      employee: this.apiService.get('/employees/' + currentUser.id),
      departments: this.apiService.get('/departments')
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        const empRaw = data.employee;
        this.employee = empRaw?.data || (empRaw && !empRaw.success ? empRaw : null) || empRaw || {};
        const deptRaw = data.departments;
        const deptData = deptRaw?.data ?? deptRaw;

        if (this.employee.managerId) {
          this.apiService.get('/employees/' + this.employee.managerId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (mgrRes: any) => {
              const mgr = mgrRes?.data || mgrRes || {};
              this.employee.managerName = `${mgr.firstName || ''} ${mgr.lastName || ''}`.trim();
            }
          });
        }

        this.departments = Array.isArray(deptData) ? deptData : [];
        this.loading = false;
        if (this.employee.presentCountry) this.onCountryChange('present', this.employee.presentCountry);
        if (this.employee.permanentCountry) this.onCountryChange('permanent', this.employee.permanentCountry);
        this.loadDocuments();
        this.loadTeamMembers();
        this.loadCandidateFormData();
      },
      error: () => {
        this.employee = currentUser;
        this.loading = false;
      }
    });
  }

  loadTeamMembers(): void {
    if (!this.employee?.departmentId) return;
    this.apiService.get('/employees').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const list: any[] = res?.data ?? res ?? [];
        this.teamMembers = list
          .filter((e: any) => e.departmentId === this.employee.departmentId && e.id !== this.employee.id)
          .map((e: any) => ({
            ...e,
            initials: ((e.firstName?.[0] || '') + (e.lastName?.[0] || '')).toUpperCase()
          }));
      },
      error: () => { this.teamMembers = []; }
    });
  }

  loadCandidateFormData(): void {
    if (!this.employee?.id) return;
    this.apiService.get('/candidates/by-employee/' + this.employee.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const candidate = res?.data || res;
        if (candidate?.formData) {
          try {
            const fd = typeof candidate.formData === 'string' ? JSON.parse(candidate.formData) : candidate.formData;
            this.candidateFormData = fd;
            this.candidateEducation = fd.educationRows || [];
            this.candidateExperience = fd.experienceRows || [];
          } catch (e) {}
        }
      },
      error: () => {}
    });
  }

  loadDocuments(): void {
    if (!this.employee?.id) return;
    this.apiService.get('/employee-documents/employee/' + this.employee.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const list = res?.data ?? res ?? [];
        this.documents = list.filter((d: any) => d.status === 'ACTIVE');
      },
      error: () => { this.documents = []; }
    });
  }

  getDeptName(): string {
    const d = this.departments.find(dept => dept.id === this.employee.departmentId);
    return d ? d.name : '—';
  }

  getInitials(): string {
    const f = (this.employee.firstName || '')[0] || '';
    const l = (this.employee.lastName || '')[0] || '';
    return (f + l).toUpperCase();
  }

  toggleEdit(): void {
    this.editMode = !this.editMode;
  }

  saveProfile(): void {
    this.saving = true;
    const currentUser = this.authService.getCurrentUser();
    const isEmployee = (currentUser?.role || '').toLowerCase() === 'employee';
    const url = isEmployee ? '/employees/profile/me' : '/employees/' + this.employee.id;
    this.apiService.patch(url, this.employee).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.snackbarService.showSuccess('Profile updated successfully');
        this.editMode = false;
        this.saving = false;
      },
      error: () => {
        this.snackbarService.showFailure('Failed to update profile');
        this.saving = false;
      }
    });
  }

  onProfilePictureChange(event: any): void {
    const file = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      this.snackbarService.showFailure('Profile picture must be less than 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.employee.profilePicture = reader.result as string;
      this.snackbarService.showSuccess('Profile picture updated. Save to persist.');
    };
    reader.readAsDataURL(file);
  }

  getDoc(docKey: string): any {
    return this.documents.find(d => d.documentType === docKey) || null;
  }

  getDocumentStatus(docKey: string): string {
    return this.getDoc(docKey) ? 'uploaded' : 'pending';
  }

  getDocumentDate(docKey: string): string {
    const doc = this.getDoc(docKey);
    if (!doc?.uploadedAt) return '';
    return new Date(doc.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  uploading: Record<string, boolean> = {};

  onDocumentUpload(event: any, docType: string): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      this.snackbarService.showFailure('File size must be less than 5 MB');
      return;
    }
    if (!this.employee?.id) return;

    this.uploading[docType] = true;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', docType);

    this.http.post(
      `${this.getApiBase()}/employee-documents/employee/${this.employee.id}/upload`,
      formData,
      { headers: this.getAuthHeaders() }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const saved = res?.data ?? res;
        const idx = this.documents.findIndex(d => d.documentType === docType);
        if (idx >= 0) this.documents[idx] = saved;
        else this.documents.push(saved);
        this.uploading[docType] = false;
        this.snackbarService.showSuccess(`${this.getDocLabel(docType)} uploaded successfully`);
        event.target.value = '';
      },
      error: () => {
        this.uploading[docType] = false;
        this.snackbarService.showFailure('Upload failed. Please try again.');
      }
    });
  }

  downloadDocument(docKey: string): void {
    const doc = this.getDoc(docKey);
    if (!doc) return;
    this.http.get(
      `${this.getApiBase()}/employee-documents/${doc.id}/download`,
      { headers: this.getAuthHeaders() }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const base64 = res?.fileContent;
        const mime = res?.mimeType || 'application/octet-stream';
        const fileName = res?.fileName || doc.fileName;
        const blob = this.base64ToBlob(base64, mime);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = fileName; a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.snackbarService.showFailure('Download failed')
    });
  }

  deleteDocument(docKey: string): void {
    const doc = this.getDoc(docKey);
    if (!doc) return;
    this.http.delete(
      `${this.getApiBase()}/employee-documents/${doc.id}`,
      { headers: this.getAuthHeaders() }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.documents = this.documents.filter(d => d.documentType !== docKey);
        this.snackbarService.showSuccess('Document removed');
      },
      error: () => this.snackbarService.showFailure('Delete failed')
    });
  }

  getDocLabel(key: string): string {
    return this.documentTypes.find(d => d.key === key)?.label || key;
  }

  private base64ToBlob(base64: string, mime: string): Blob {
    const bytes = atob(base64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  private getApiBase(): string {
    return environment.apiUrl;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = sessionStorage.getItem('token') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  get profileCompletion(): number {
    const fields = [
      this.employee.firstName, this.employee.lastName, this.employee.dateOfBirth,
      this.employee.gender, this.employee.phone, this.employee.aadhaarNumber,
      this.employee.panNumber, this.employee.emergencyContactName,
      this.employee.emergencyContactPhone, this.employee.bankName,
      this.employee.bankAccount, this.employee.ifscCode,
      this.employee.presentAddressLine1, this.employee.presentCity,
      this.employee.presentCountry
    ];
    const filled = fields.filter(f => f && String(f).trim()).length;
    return Math.round((filled / fields.length) * 100);
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  loadCountries(): void {
    this.loadingCountries = true;
    this.http.get<any>(this.geoApiBase + '/countries').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.countries = (res?.data || []).map((c: any) => c.country).sort();
        this.loadingCountries = false;
      },
      error: () => {
        this.countries = ['India','United States','United Kingdom','Canada','Australia','Germany','France','Singapore','UAE'];
        this.loadingCountries = false;
      }
    });
  }

  onCountryChange(type: 'present' | 'permanent', country: string): void {
    if (type === 'present') {
      this.presentStates = [];
      this.presentCities = [];
      this.loadingPresentStates = true;
    } else {
      this.permanentStates = [];
      this.permanentCities = [];
      this.loadingPermanentStates = true;
    }
    this.http.post<any>(this.geoApiBase + '/countries/states', { country }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        const states = (res?.data?.states || []).map((s: any) => s.name).sort();
        if (type === 'present') { this.presentStates = states; this.loadingPresentStates = false; }
        else { this.permanentStates = states; this.loadingPermanentStates = false; }
        const state = type === 'present' ? this.employee.presentState : this.employee.permanentState;
        if (state && states.includes(state)) this.onStateChange(type, country, state);
      },
      error: () => {
        if (type === 'present') this.loadingPresentStates = false;
        else this.loadingPermanentStates = false;
      }
    });
  }

  onStateChange(type: 'present' | 'permanent', country: string, state: string): void {
    if (type === 'present') { this.presentCities = []; this.loadingPresentCities = true; }
    else { this.permanentCities = []; this.loadingPermanentCities = true; }
    this.http.post<any>(this.geoApiBase + '/countries/state/cities', { country, state }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        const cities = (res?.data || []).sort();
        if (type === 'present') { this.presentCities = cities; this.loadingPresentCities = false; }
        else { this.permanentCities = cities; this.loadingPermanentCities = false; }
      },
      error: () => {
        if (type === 'present') this.loadingPresentCities = false;
        else this.loadingPermanentCities = false;
      }
    });
  }
}
